import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { FaServer, FaCloud, FaRobot, FaPython, FaDatabase, FaAws, FaMicrosoft } from 'react-icons/fa';
import { SiPytorch, SiTensorflow, SiApachespark, SiDatabricks, SiPostgresql, SiMysql, SiMongodb, SiTerraform } from 'react-icons/si';
import { DiRuby, DiScala } from 'react-icons/di';

const About = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.1 }
    );

    const aboutSection = document.querySelector('#about');
    if (aboutSection) observer.observe(aboutSection);

    return () => observer.disconnect();
  }, []);

  const skills = [
    // Data
    { name: 'PySpark', category: 'data', icon: <SiApachespark /> },
    { name: 'Databricks', category: 'data', icon: <SiDatabricks /> },
    { name: 'SQL', category: 'data', icon: <FaDatabase /> },
    { name: 'PostgreSQL', category: 'data', icon: <SiPostgresql /> },
    { name: 'MySQL', category: 'data', icon: <SiMysql /> },
    { name: 'MongoDB', category: 'data', icon: <SiMongodb /> },
    
    // Cloud
    { name: 'AWS', category: 'cloud', icon: <FaAws /> },
    { name: 'Azure', category: 'cloud', icon: <FaMicrosoft /> },
    
    // DevOps & Infrastructure
    { name: 'Terraform', category: 'devops', icon: <SiTerraform /> },
    
    // AI/ML
    { name: 'LLMs', category: 'ai', icon: <FaRobot /> },
    { name: 'Machine Learning', category: 'ai', icon: <SiTensorflow /> },
    { name: 'Deep Learning', category: 'ai', icon: <SiPytorch /> },
    
    // Language
    { name: 'Python', category: 'language', icon: <FaPython /> },
  ];

  const [activeCategory, setActiveCategory] = useState('all');

  const filteredSkills = activeCategory === 'all' 
    ? skills 
    : skills.filter(skill => skill.category === activeCategory);

  return (
    <AboutWrapper id="about" className={isVisible ? 'visible' : ''}>
      <div className="container">
        <SectionTitle>About Me</SectionTitle>
        <AboutContent>
          <AboutText>
            <IntroText>
              <p>From building my first program in QBasic as a curious 10-year-old to designing enterprise-grade data solutions today, my journey in tech has always been driven by passion and purpose.</p>
              
              <p>I'm currently a <Highlight>Lead Data Engineer at John Deere</Highlight>, with over 6 years of experience in big data, cloud platforms (AWS, Azure), and GenAI. I specialize in building resilient, scalable data pipelines and AI-powered solutions.</p>
            </IntroText>
            
            <Journey>
              <h3>My Journey</h3>
              <Timeline>
                <TimelineItem>
                  <TimelineDot />
                  <TimelineContent>
                    <h4>Early Start</h4>
                    <p>MSCIT certification in 5th grade, explored C/C++ in middle school</p>
                  </TimelineContent>
                </TimelineItem>
                <TimelineItem>
                  <TimelineDot />
                  <TimelineContent>
                    <h4>Web Development</h4>
                    <p>Discovered HTML, CSS, and JavaScript in high school</p>
                  </TimelineContent>
                </TimelineItem>
                <TimelineItem>
                  <TimelineDot />
                  <TimelineContent>
                    <h4>Bessi Management System</h4>
                    <p>Developed an Android application with Firebase Realtime Database for efficient management during my engineering studies</p>
                  </TimelineContent>
                </TimelineItem>
                <TimelineItem>
                  <TimelineDot />
                  <TimelineContent>
                    <h4>Research Publication</h4>
                    <p>Published paper on "Digital In-store Merchandising" in the International Journal of Scientific Research in Science, Engineering and Technology</p>
                    <a href="https://res.ijsrset.com/PDF.php?pid=6372&v=5&i=10&y=2020&m=March-April" target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', textDecoration: 'none' }}>
                      View Publication
                    </a>
                  </TimelineContent>
                </TimelineItem>
                <TimelineItem>
                  <TimelineDot />
                  <TimelineContent>
                    <h4>Professional Growth</h4>
                    <p>Worked at DigitalBuzz, Xoriant, Thoughtworks and now at John Deere</p>
                  </TimelineContent>
                </TimelineItem>
              </Timeline>
            </Journey>

            <SkillsSection>
              <h3>Technical Skills</h3>
              <CategoryFilters>
                <CategoryButton 
                  active={activeCategory === 'all'}
                  onClick={() => setActiveCategory('all')}
                >
                <FaServer /> All
                </CategoryButton>
                <CategoryButton 
                  active={activeCategory === 'data'}
                  onClick={() => setActiveCategory('data')}
                >
                <FaServer /> Data
                </CategoryButton>
                <CategoryButton 
                  active={activeCategory === 'language'}
                  onClick={() => setActiveCategory('language')}
                >
                <FaServer /> Language
                </CategoryButton>
                <CategoryButton 
                  active={activeCategory === 'cloud'}
                  onClick={() => setActiveCategory('cloud')}
                >
                <FaCloud /> Cloud
                </CategoryButton>
                <CategoryButton 
                  active={activeCategory === 'ai'}
                  onClick={() => setActiveCategory('ai')}
                >
                <FaRobot /> AI/ML
                </CategoryButton>
                <CategoryButton 
                  active={activeCategory === 'devops'}
                  onClick={() => setActiveCategory('devops')}
                >
                <FaServer /> DevOps & Infra
                </CategoryButton>
              </CategoryFilters>
              <SkillsGrid>
                {filteredSkills.map((skill, index) => (
                  <SkillTag key={skill.name} delay={index * 0.1}>
                    <SkillIcon>{skill.icon}</SkillIcon>
                    {skill.name}
                  </SkillTag>
                ))}
              </SkillsGrid>
            </SkillsSection>
          </AboutText>
          
          <VisualSection>
            <DecorativeElement />
            <ProfileImage 
              src="/path-to-your-image.jpg" 
              alt="Profile" 
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </VisualSection>
        </AboutContent>
      </div>
    </AboutWrapper>
  );
};

// Animations
const fadeIn = keyframes`
  from { 
    opacity: 0; 
    transform: translateY(20px); 
  }
  to { 
    opacity: 1; 
    transform: translateY(0); 
  }
`;

const float = keyframes`
  0% { 
    transform: translateY(0px); 
  }
  50% { 
    transform: translateY(-10px); 
  }
  100% { 
    transform: translateY(0px); 
  }
`;

const morph = keyframes`
  0% { 
    border-radius: 30% 70% 70% 30% / 30% 30% 70% 70%; 
  }
  50% { 
    border-radius: 70% 30% 30% 70% / 70% 70% 30% 30%; 
  }
  100% { 
    border-radius: 30% 70% 70% 30% / 30% 30% 70% 70%; 
  }
`;

// Styled Components
const AboutWrapper = styled.section`
  background-color: ${({ theme }) => theme.colors.background};
  padding: 6rem 0;
  opacity: 0;
  transform: translateY(20px);
  transition: opacity 0.6s ease-out, transform 0.6s ease-out;
  
  &.visible {
    opacity: 1;
    transform: translateY(0);
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 4rem 0;
  }
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  color: ${({ theme }) => theme.colors.primary};
  text-align: center;
  margin-bottom: 3rem;
  position: relative;
  display: inline-block;
  left: 50%;
  transform: translateX(-50%);
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 100%;
    height: 4px;
    background: linear-gradient(90deg, ${({ theme }) => theme.colors.primary}, transparent);
    border-radius: 2px;
  }
`;

const AboutContent = styled.div`
  display: grid;
  grid-template-columns: 1.2fr 1fr;
  gap: 4rem;
  align-items: start;
  margin-top: 2rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
    gap: 3rem;
  }
`;

const AboutText = styled.div`
  h3 {
    color: ${({ theme }) => theme.colors.text};
    margin: 2rem 0 1.5rem;
    font-size: 1.5rem;
    position: relative;
    display: inline-block;
    
    &::after {
      content: '';
      position: absolute;
      bottom: -5px;
      left: 0;
      width: 50px;
      height: 3px;
      background: ${({ theme }) => theme.colors.primary};
      border-radius: 2px;
    }
  }
`;

const IntroText = styled.div`
  p {
    font-size: 1.1rem;
    line-height: 1.8;
    color: ${({ theme }) => theme.colors.text};
    margin-bottom: 1.5rem;
    opacity: 0;
    animation: ${fadeIn} 0.6s ease-out forwards;
    
    &:nth-child(1) { 
      animation-delay: 0.2s; 
    }
    &:nth-child(2) { 
      animation-delay: 0.4s; 
    }
  }
`;

const Highlight = styled.span`
  color: ${({ theme }) => theme.colors?.primary || '#2563eb'};
  font-weight: 600;
  white-space: nowrap;
  display: inline-block;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    bottom: 2px;
    left: 0;
    width: 100%;
    height: 30%;
    background-color: ${({ theme }) => `${theme.colors?.primary || '#2563eb'}33`};
    z-index: -1;
    opacity: 0.5;
    border-radius: 2px;
    transition: all 0.3s ease;
  }
  
  &:hover::before {
    height: 100%;
    opacity: 0.2;
  }
`;

const Journey = styled.div`
  margin: 3rem 0;
  opacity: 0;
  animation: ${fadeIn} 0.6s ease-out 0.6s forwards;
`;

const Timeline = styled.div`
  position: relative;
  padding-left: 2rem;
  margin-top: 1.5rem;
  
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 10px;
    height: calc(100% - 20px);
    width: 2px;
    background: ${({ theme }) => theme.colors.primary};
    opacity: 0.3;
  }
`;

const TimelineItem = styled.div`
  position: relative;
  margin-bottom: 2rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const TimelineDot = styled.div`
  position: absolute;
  left: -2.5rem;
  top: 5px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.primary};
  border: 2px solid white;
  box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primary};
`;

const TimelineContent = styled.div`
  h4 {
    color: ${({ theme }) => theme.colors.text};
    margin-bottom: 0.5rem;
    font-size: 1.1rem;
  }
  
  p {
    color: ${({ theme }) => theme.colors.textLight};
    margin: 0;
    font-size: 0.95rem;
    line-height: 1.6;
  }
`;

const SkillsSection = styled.div`
  margin-top: 3rem;
  opacity: 0;
  animation: ${fadeIn} 0.6s ease-out 0.8s forwards;
`;

const CategoryFilters = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.8rem;
  margin: 1.5rem 0;
`;

const CategoryButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1.2rem;
  border-radius: 50px;
  border: 2px solid ${({ theme }) => theme.colors.primary};
  background: ${({ active, theme }) => 
    active ? theme.colors.primary : 'transparent'};
  color: ${({ active, theme }) => 
    active ? '#fff' : theme.colors.primary};
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.9rem;
  
  &:hover {
    background: ${({ theme }) => theme.colors.primary};
    color: #fff;
    transform: translateY(-2px);
  }
  
  svg {
    font-size: 0.9rem;
  }
`;

const SkillsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 1rem;
  margin-top: 1.5rem;
`;

const SkillTag = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: ${({ theme }) => theme.colors?.backgroundLight || '#f3f4f6'};
  color: ${({ theme }) => theme.colors?.text || '#1f2937'};
  padding: 0.6rem 1rem;
  border-radius: 8px;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  opacity: 0;
  transform: translateY(10px);
  animation: ${fadeIn} 0.5s ease forwards;
  animation-delay: ${({ delay }) => delay || 0}s;
  
  &:hover {
    background: ${({ theme }) => theme.colors?.primary || '#2563eb'};
    color: white;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    
    svg {
      color: white;
    }
  }
`;

const SkillIcon = styled.span`
  display: flex;
  align-items: center;
  font-size: 1.1rem;
  color: ${({ theme }) => theme.colors?.primary || '#2563eb'};
`;

const VisualSection = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  opacity: 0;
  animation: ${fadeIn} 0.6s ease-out 0.4s forwards;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    margin-top: 2rem;
  }
`;

const ProfileImage = styled.img`
  width: 100%;
  max-width: 350px;
  border-radius: 10px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 
              0 10px 10px -5px rgba(0, 0, 0, 0.04);
  position: relative;
  z-index: 2;
  transition: transform 0.5s ease, box-shadow 0.5s ease;
  animation: ${float} 6s ease-in-out infinite;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.15);
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    max-width: 300px;
  }
`;
  


const DecorativeElement = styled.div`
  position: absolute;
  width: 200px;
  height: 200px;
  background: ${({ theme }) => theme.colors.primary};
  opacity: 0.05;
  border-radius: 30% 70% 70% 30% / 30% 30% 70% 70%;
  z-index: 1;
  animation: ${morph} 8s ease-in-out infinite;
  
  &::before {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    background: ${({ theme }) => theme.colors.secondary};
    opacity: 0.03;
    border-radius: inherit;
    animation: ${morph} 12s ease-in-out infinite reverse;
  }
`;

export default About;
